import { inject, Injectable } from '@angular/core';
import {
  addDoc,
  collection, deleteDoc,
  doc,
  Firestore,
  getDoc,
  getDocs,
  Query,
  query,
  updateDoc,
  where
} from '@angular/fire/firestore';
import { ObjectHelper } from "../helpers/object.helper";
import { ScriptError } from "../models/script-error.model";

@Injectable({
  providedIn: 'root',
})
export class ScriptErrorService {

  private firestore = inject(Firestore);

  /**
   * Create a query to list organization scripts errors
   * @param organizationId Current organization id
   * @returns Organization scripts errors query
   */
  list(organizationId: string): Query<ScriptError> {
    return query(collection(this.firestore, 'scripts-errors'),
      where('organizationId', '==', organizationId)).withConverter(ScriptError.converter);
  }

  /**
   * List scripts errors
   * @param organizationId Current organization id
   * @returns Native and organization scripts errors
   */
  async listSync(organizationId: string)
    : Promise<ScriptError[]> {
    const q = this.list(organizationId);
    const snapshot = await getDocs(q);
    let organizationScripts = [];
    if (!snapshot.empty) {
      organizationScripts = snapshot.docs.map(doc => doc.data());
      organizationScripts.sort((a: ScriptError, b: ScriptError) => a.code.localeCompare(b.code));
    }
    return organizationScripts;
  }

  /**
   * Check if a script error code is used
   * @param organizationId The organization id
   * @param code The script error code to check
   * @returns True if code is already used
   */
  async checkByCode(organizationId: string, code: string): Promise<boolean> {
    const q = query(collection(this.firestore, 'scripts-errors'),
      where('code', '==', code),
      where('organizationId', '==', organizationId)).withConverter(ScriptError.converter);
    const get = await getDocs(q);
    return !get.empty;
  }

  /**
   * Create a script error
   * @param scriptError The script error to create
   * @returns Id of the created script error
   */
  async create(scriptError: ScriptError): Promise<string> {
    const ref = collection(this.firestore, 'scripts-errors').withConverter(ScriptError.converter);
    const created = await addDoc(ref, scriptError);
    return created.id;
  }

  /**
   * Update a script error
   * @param id The script error id to update
   * @param scriptError The updated script error
   */
  async update(id: string, scriptError: Partial<ScriptError>): Promise<void> {
    const ref = doc(this.firestore, 'scripts-errors', id).withConverter(ScriptError.converter);
    await updateDoc(ref, ObjectHelper.anonymize(scriptError));
  }

  /**
   * Get a script error by id
   * @param id The script error id to retrieve
   * @returns The script error or null
   */
  async get(id: string): Promise<ScriptError | null> {
    if (!id) {
      return null;
    }
    const ref = doc(this.firestore, 'scripts-errors', id).withConverter(ScriptError.converter);
    const document = await getDoc(ref);
    return document.data();
  }

  /**
   * Delete a script error by id
   * @param id The script error id to delete
   */
  async delete(id: string): Promise<void> {
    const ref = doc(this.firestore, 'scripts-errors', id).withConverter(ScriptError.converter);
    await deleteDoc(ref);
  }
}
